import React, { useState } from 'react';
import './DemoRequestForm.css';

interface DemoRequestFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess?: () => void;
}

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  company: string;
  country: string;
  message: string;
}

const DemoRequestForm: React.FC<DemoRequestFormProps> = ({ isOpen, onClose, onSubmitSuccess }) => {
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    country: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.fullName.trim() || !formData.email.trim()) {
      setError('Full name and email are required');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const response = await fetch(`${apiUrl}/api/v1/demo-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          full_name: formData.fullName,
          email: formData.email,
          phone: formData.phone || undefined,
          company: formData.company || undefined,
          country: formData.country || undefined,
          message: formData.message || undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit demo request');
      }

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          company: '',
          country: '',
          message: '',
        });
        onClose();
        if (onSubmitSuccess) {
          onSubmitSuccess();
        }
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to submit demo request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="demo-form-overlay" onClick={onClose}>
      <div className="demo-form-container" onClick={(e) => e.stopPropagation()}>
        <button className="demo-form-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        
        <div className="demo-form-content">
          <h2 className="demo-form-title">Book a Demo</h2>
          <p className="demo-form-subtitle">
            Fill out the form below and we'll get back to you soon!
          </p>

          {error && (
            <div className="demo-form-error">
              {error}
            </div>
          )}

          {success && (
            <div className="demo-form-success">
              Thank you! Your demo request has been submitted successfully. We'll contact you soon.
            </div>
          )}

          <form onSubmit={handleSubmit} className="demo-form">
            <div className="demo-form-group">
              <label htmlFor="fullName" className="demo-form-label">
                Full Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                className="demo-form-input"
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="demo-form-group">
              <label htmlFor="email" className="demo-form-label">
                Email <span className="required">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="demo-form-input"
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="demo-form-row">
              <div className="demo-form-group">
                <label htmlFor="phone" className="demo-form-label">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="demo-form-input"
                  disabled={isSubmitting}
                />
              </div>

              <div className="demo-form-group">
                <label htmlFor="country" className="demo-form-label">Country</label>
                <select
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className="demo-form-input"
                  disabled={isSubmitting}
                >
                  <option value="">Select your country</option>
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                  <option value="GB">United Kingdom</option>
                  <option value="AU">Australia</option>
                  <option value="DE">Germany</option>
                  <option value="FR">France</option>
                  <option value="IT">Italy</option>
                  <option value="ES">Spain</option>
                  <option value="NL">Netherlands</option>
                  <option value="SE">Sweden</option>
                  <option value="NO">Norway</option>
                  <option value="DK">Denmark</option>
                  <option value="FI">Finland</option>
                  <option value="CH">Switzerland</option>
                  <option value="AT">Austria</option>
                  <option value="BE">Belgium</option>
                  <option value="IE">Ireland</option>
                  <option value="PT">Portugal</option>
                  <option value="GR">Greece</option>
                  <option value="PL">Poland</option>
                  <option value="CZ">Czech Republic</option>
                  <option value="HU">Hungary</option>
                  <option value="RO">Romania</option>
                  <option value="BG">Bulgaria</option>
                  <option value="HR">Croatia</option>
                  <option value="SI">Slovenia</option>
                  <option value="SK">Slovakia</option>
                  <option value="LT">Lithuania</option>
                  <option value="LV">Latvia</option>
                  <option value="EE">Estonia</option>
                  <option value="MT">Malta</option>
                  <option value="CY">Cyprus</option>
                  <option value="LU">Luxembourg</option>
                  <option value="IS">Iceland</option>
                  <option value="JP">Japan</option>
                  <option value="KR">South Korea</option>
                  <option value="CN">China</option>
                  <option value="IN">India</option>
                  <option value="SG">Singapore</option>
                  <option value="MY">Malaysia</option>
                  <option value="TH">Thailand</option>
                  <option value="PH">Philippines</option>
                  <option value="ID">Indonesia</option>
                  <option value="VN">Vietnam</option>
                  <option value="NZ">New Zealand</option>
                  <option value="ZA">South Africa</option>
                  <option value="AE">United Arab Emirates</option>
                  <option value="SA">Saudi Arabia</option>
                  <option value="IL">Israel</option>
                  <option value="TR">Turkey</option>
                  <option value="BR">Brazil</option>
                  <option value="MX">Mexico</option>
                  <option value="AR">Argentina</option>
                  <option value="CL">Chile</option>
                  <option value="CO">Colombia</option>
                  <option value="PE">Peru</option>
                  <option value="RU">Russia</option>
                  <option value="UA">Ukraine</option>
                  <option value="BY">Belarus</option>
                  <option value="KZ">Kazakhstan</option>
                  <option value="EG">Egypt</option>
                  <option value="NG">Nigeria</option>
                  <option value="KE">Kenya</option>
                  <option value="GH">Ghana</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div className="demo-form-group">
              <label htmlFor="company" className="demo-form-label">Company</label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="demo-form-input"
                disabled={isSubmitting}
              />
            </div>

            <div className="demo-form-group">
              <label htmlFor="message" className="demo-form-label">Message</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                className="demo-form-textarea"
                rows={4}
                disabled={isSubmitting}
              />
            </div>

            <div className="demo-form-actions">
              <button
                type="button"
                className="demo-form-button secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="demo-form-button primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DemoRequestForm;

